import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import useSEO from '../hooks/useSEO';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const SubcategoryProductsPage = () => {
    const { category, subcategory } = useParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedFaq, setExpandedFaq] = useState(null);

    const formatName = (slug) =>
        decodeURIComponent(slug || '')
            .split(/[-_]/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');

    const categoryName = formatName(category);
    const subcategoryName = decodeURIComponent(subcategory || '');

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiService.request(
                    `/products?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategoryName)}&limit=50`
                );
                if (response.data?.products) {
                    setProducts(response.data.products.filter(p => p.isActive !== false));
                } else {
                    setProducts([]);
                }
            } catch (err) {
                console.error('Failed to fetch products:', err);
                setError('Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [category, subcategoryName]);

    useSEO({
        title: `${subcategoryName} | ${categoryName} | Kigo Prints`,
        description: `Premium ${subcategoryName.toLowerCase()} printing services under ${categoryName.toLowerCase()}. Professional quality and fast delivery.`,
    });

    const toggleFaq = (index) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-14 w-14 border-4 border-green-200 border-t-[#008000]" />
                    <p className="text-gray-500 font-medium">Loading products…</p>
                </div>
            </div>
        );
    }

    const heroImage = products[0]?.images?.[0]?.url || 'https://via.placeholder.com/1200x600/10b981/ffffff?text=' + subcategoryName;

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            {/* Hero Section */}
            <section className="relative py-12 md:py-24 px-7 md:px-4 pt-24 md:pt-32 min-h-[350px] md:min-h-[600px] flex items-center">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${heroImage})` }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-50" />

                <div className="max-w-7xl mx-auto relative z-10 text-center sm:text-left w-full animate-fade-in">
                    <div className="max-w-4xl mx-auto sm:mx-0">
                        <nav className="text-sm mb-4 text-gray-300 flex items-center gap-1.5 flex-wrap justify-center sm:justify-start">
                            <Link to="/" className="hover:text-white transition-colors">Home</Link>
                            <span>/</span>
                            <Link to={`/subcategory/${category}`} className="hover:text-white transition-colors">
                                {categoryName}
                            </Link>
                            <span>/</span>
                            <span className="text-white font-medium">{subcategoryName}</span>
                        </nav>
                        <h1 className="text-3xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight px-2 sm:px-0 animate-slide-up">
                            {subcategoryName}
                        </h1>
                        <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed px-3 sm:px-0 animate-slide-up animation-delay-200">
                            {products.length > 0
                                ? `Discover our range of ${products.length} premium ${subcategoryName.toLowerCase()} types, crafted for excellence.`
                                : `Custom ${subcategoryName.toLowerCase()} printing solutions for your business.`}
                        </p>
                    </div>
                </div>
            </section>

            {/* Products Grid Section */}
            <section className="py-8 md:py-16 px-5 md:px-4 bg-white animate-fade-in">
                <div className="max-w-7xl mx-auto">
                    {error ? (
                        <div className="text-center py-20">
                            <p className="text-red-500 font-medium mb-4">{error}</p>
                            <button
                                onClick={() => navigate(`/subcategory/${category}`)}
                                className="text-[#008000] hover:text-[#008000] font-semibold"
                            >
                                ← Back to {categoryName}
                            </button>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Products Found</h2>
                            <p className="text-gray-500 mb-6">There are no products in <strong>{subcategoryName}</strong> yet.</p>
                            <Link
                                to={`/subcategory/${category}`}
                                className="inline-block bg-[#008000] text-white px-6 py-3 rounded-xl hover:bg-[#008000] transition-colors font-semibold"
                            >
                                ← Back to {categoryName}
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-8">
                            {products.map((product, productIndex) => {
                                const price = product.pricing?.[0];
                                const displayPrice = price?.discountPrice || price?.basePrice;
                                const originalPrice = price?.discountPrice ? price?.basePrice : null;
                                const productFeatures = product.features?.slice(0, 3) || [
                                    'High-quality printing',
                                    'Custom sizes available',
                                    'Premium paper stock'
                                ];

                                return (
                                    <div key={product._id} className="bg-white rounded-xl shadow-lg  hover:shadow-xl transition-all duration-300 hover:scale-[1.03] flex opacity-0 animate-fade-in-up" style={{ animationDelay: `${productIndex * 50}ms`, animationFillMode: 'forwards' }}>
                                        <Link to={`/product/${product._id}`} className="block">
                                            <div className="w-full aspect-square overflow-hidden relative">
                                                {product.images?.[0]?.url ? (
                                                    <img
                                                        src={product.images[0].url}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                                        <svg className="w-16 h-16 md:w-24 md:h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                                {price?.discountPrice && (
                                                    <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
                                                        {Math.round((1 - price.discountPrice / price.basePrice) * 100)}% OFF
                                                    </div>
                                                )}
                                            </div>
                                        </Link>
                                        <div className="p-3 md:p-6 flex flex-col justify-between flex-grow">
                                            <div>
                                                <div className="flex justify-between items-start mb-1.5 md:mb-2">
                                                    <Link to={`/product/${product._id}`} className="block flex-1 pr-2">
                                                        <h3 className="text-sm md:text-xl font-semibold hover:text-[#008000] transition-colors line-clamp-2">{product.name}</h3>
                                                    </Link>
                                                    {displayPrice && (
                                                        <div className="text-right flex-shrink-0">
                                                            <div className="text-sm md:text-lg font-bold text-[#008000]">₹{displayPrice}</div>
                                                            {originalPrice && (
                                                                <div className="text-[10px] md:text-xs text-gray-400 line-through">₹{originalPrice}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs md:text-base text-gray-600 mb-2 md:mb-4 line-clamp-2">{product.description}</p>
                                                <ul className="text-xs md:text-sm text-gray-500 mb-2.5 md:mb-4 space-y-0.5">
                                                    {productFeatures.map((feature, idx) => (
                                                        <li key={idx} className="flex items-start">
                                                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2 flex-shrink-0 mt-1"></span>
                                                            <span className="text-xs md:text-sm line-clamp-2">{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <Link to={`/product/${product._id}`} className="block mt-auto">
                                                <button className="w-full bg-[#008000] text-white px-5 md:px-8 py-2.5 md:py-4 rounded-xl hover:bg-[#008000] font-semibold text-sm md:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                                                    View Details
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* How to Design Section */}
            <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-8 md:mb-16">
                        <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">{subcategoryName} Design</h2>
                        <p className="text-sm md:text-lg text-gray-600 px-3">
                            Whether you're a beginner or a professional designer, we have {subcategoryName} design options for any skill level.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                        {/* Upload Design Option */}
                        <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
                            <div className="bg-blue-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                                <div className="w-12 h-12 md:w-24 md:h-24 bg-blue-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                                    <svg className="w-8 h-8 md:w-12 md:h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                </div>
                                <div className="flex flex-wrap gap-1 md:gap-2 justify-center">
                                    <div className="bg-red-100 text-red-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">pdf</div>
                                    <div className="bg-orange-100 text-orange-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">jpg</div>
                                    <div className="bg-purple-100 text-purple-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">png</div>
                                    <div className="bg-yellow-100 text-yellow-600 px-2 py-0.5 md:px-3 md:py-1 rounded text-xs font-medium">gif</div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Upload a full design if you...</h3>
                            <ul className="text-xs md:text-base text-gray-600 space-y-1 md:space-y-2 mb-4 md:mb-6">
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Already have a complete design
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Have design chops (or your own designer)
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Are confident with your {subcategoryName} layout
                                </li>
                            </ul>
                        </div>

                        {/* Online Design Option */}
                        <div className="bg-white rounded-xl px-5 p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
                            <div className="bg-yellow-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                                <div className="w-12 h-12 md:w-24 md:h-24 bg-yellow-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                                    <svg className="w-8 h-8 md:w-12 md:h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Design your {subcategoryName} online if you...</h3>
                            <ul className="text-xs md:text-base text-gray-600 space-y-1 md:space-y-2 mb-4 md:mb-6">
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Already have your own logo
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Want to create your own {subcategoryName} design easily
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Want to customise every detail
                                </li>
                            </ul>
                        </div>

                        {/* Templates Option */}
                        <div className="bg-white rounded-xl px-5 p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
                            <div className="bg-purple-100 rounded-xl p-3 md:p-6 mb-4 md:mb-6 flex flex-col items-center">
                                <div className="w-12 h-12 md:w-24 md:h-24 bg-purple-200 rounded-xl flex items-center justify-center mb-2 md:mb-4">
                                    <div className="grid grid-cols-2 gap-1">
                                        <div className="w-6 h-6 bg-white border border-gray-300 rounded"></div>
                                        <div className="w-6 h-6 bg-gray-400 rounded"></div>
                                        <div className="w-6 h-6 bg-gray-400 rounded"></div>
                                        <div className="w-6 h-6 bg-white border border-gray-300 rounded flex items-center justify-center">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Use our templates if you...</h3>
                            <ul className="text-xs md:text-base text-gray-600 space-y-1 mb-4 md:mb-6">
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Are looking for inspiration
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Want a professional, quality {subcategoryName} design
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Want simple, fast customisation for quick card printing
                                </li>
                                <li className="flex items-start">
                                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-2 md:mr-3 mt-1.5 md:mt-2 flex-shrink-0"></span>
                                    Are creating Small or Medium {subcategoryName}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* WhyKigo Print Section */}
            <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-8 md:mb-16">
                        <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Why Kigo Print {subcategoryName}?</h2>
                        <p className="text-sm md:text-lg text-gray-600 px-3">
                            From our products to our service, you'll feel the Kigo Prints difference at every step.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {/* Next Day Service */}
                        <div className="text-center">
                            <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                                <svg className="w-8 h-8 md:w-12 md:h-12 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-[#008000] text-white text-xs px-1.5 py-0.5 md:px-2 md:py-1 rounded-full font-bold">24</div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Next day {subcategoryName}!</h3>
                            <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                                Next-day, fast {subcategoryName} printing is available on Original orders before 2pm (EST) Mon-Fri.
                            </p>
                        </div>

                        {/* Printed in India */}
                        <div className="text-center">
                            <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                                <div className="w-16 h-12 bg-gradient-to-r from-orange-500 via-white to-green-500 rounded border-2 border-gray-300 flex items-center justify-center">
                                    <div className="w-6 h-6 border-2 border-blue-600 rounded-full flex items-center justify-center">
                                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Printed in India</h3>
                            <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                                Designed by you and Kigo Prints, printed in India with the highest quality standards.
                            </p>
                        </div>

                        {/* Printfinity Technology */}
                        <div className="text-center">
                            <div className="bg-green-100 rounded-xl p-3 md:p-6 mx-auto mb-3 md:mb-6 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center relative">
                                <div className="grid grid-cols-2 gap-1">
                                    <div className="w-4 h-4 bg-[#008000] rounded"></div>
                                    <div className="w-4 h-4 bg-green-400 rounded"></div>
                                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                                    <div className="w-4 h-4 bg-green-300 rounded"></div>
                                </div>
                            </div>
                            <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Kigo Prints makes every unit unique</h3>
                            <p className="text-xs md:text-base text-gray-600 leading-relaxed px-3">
                                Print a different image or full color on the back of every single piece at no extra cost for even more creative designs.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-8 md:py-16 px-5 md:px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-6 md:mb-12">
                        <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4 px-2">Frequently Asked Questions</h2>
                        <p className="text-sm md:text-lg text-gray-600 px-3">
                            Everything you need to know about our {subcategoryName} printing services
                        </p>
                    </div>
                    <div className="space-y-3 md:space-y-6">
                        {[
                            {
                                q: `What file formats do you accept for ${subcategoryName}?`,
                                a: "We accept PDF, JPG, PNG, and GIF files. For best results, please upload high-resolution files (300 DPI minimum) in CMYK color mode."
                            },
                            {
                                q: `How long does it take to print my ${subcategoryName}?`,
                                a: "Standard orders take 3-5 business days. Next-day printing is available for Original paper orders placed before 2pm EST, Monday through Friday."
                            },
                            {
                                q: "Can I see a proof before my order is printed?",
                                a: "Yes! We provide a digital proof for every order which you can approve before we initiate the printing process."
                            },
                            {
                                q: "Do you offer bulk discounts?",
                                a: "Yes! The more you order, the more you save. Our pricing automatically adjusts based on quantity, with significant savings on larger orders."
                            },
                            {
                                q: "Can I get a proof before printing?",
                                a: "Absolutely! We provide digital proofs for all orders. You can review and approve your design before we proceed with printing to ensure everything looks perfect."
                            },
                            {
                                q: "What if I'm not satisfied with my order?",
                                a: "We stand behind our quality. If you're not completely satisfied with your {categoryName}, contact our customer service team and we'll make it right."
                            }
                        ].map((faq, idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                                <div
                                    className="flex justify-between items-start cursor-pointer p-3 md:p-6"
                                    onClick={() => toggleFaq(idx)}
                                >
                                    <h3 className="text-sm md:text-lg font-semibold text-gray-800 flex-1 pr-2">{faq.q}</h3>
                                    <svg className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${expandedFaq === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {expandedFaq === idx && (
                                    <div className="px-3 md:px-6 pb-3 md:pb-6 text-gray-600 text-xs md:text-base">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Customer Reviews Section */}
            <section className="py-8 md:py-16 px-5 md:px-4 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-6 md:mb-12">
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-1 md:gap-2 mb-3 md:mb-4">
                            <div className="flex text-green-500">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-4 h-4 md:w-6 md:h-6 fill-current" viewBox="0 0 24 24">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                ))}
                            </div>
                            <span className="text-lg md:text-xl font-bold text-gray-800">4.9</span>
                            <span className="text-sm md:text-base text-gray-600">/ 5</span>
                            <span className="text-sm md:text-base text-gray-600">394 reviews</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
                        {/* Review 1 */}
                        <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
                            <div className="flex text-green-500 mb-2 md:mb-4">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Beautiful quality prints, loved the texture of the cardstock and that I could add so many custom details to my {subcategoryName}."</p>
                            <div className="text-xs md:text-sm">
                                <p className="font-semibold text-gray-800">Dhakshayini Boopalan</p>
                                <p className="text-gray-500">4 days ago</p>
                            </div>
                        </div>

                        {/* Review 2 */}
                        <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
                            <div className="flex text-green-500 mb-2 md:mb-4">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Fast delivery and excellent quality. The {subcategoryName} exceeded my expectations and the customer service was outstanding."</p>
                            <div className="text-xs md:text-sm">
                                <p className="font-semibold text-gray-800">Rajesh Kumar</p>
                                <p className="text-gray-500">1 week ago</p>
                            </div>
                        </div>

                        {/* Review 3 */}
                        <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg">
                            <div className="flex text-green-500 mb-2 md:mb-4">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-xs md:text-base text-gray-700 mb-2 md:mb-4">"Professional results at affordable prices. Will definitely order again for all my printing needs."</p>
                            <div className="text-xs md:text-sm">
                                <p className="font-semibold text-gray-800">Priya Sharma</p>
                                <p className="text-gray-500">2 weeks ago</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Free Delivery Banner */}
            <section className="bg-gradient-to-r from-green-50 to-emerald-50 py-4 md:py-6 px-3 md:px-4 border-t border-b border-green-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-center gap-2 md:gap-3">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-[#008000] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <p className="text-sm md:text-base font-semibold text-[#008000]">
                            Free delivery on orders above Rs.500
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gray-100 py-8 md:py-16 px-3 md:px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-xl md:text-3xl font-bold mb-2 md:mb-4 px-2">Ready to get started?</h2>
                    <p className="text-sm md:text-lg text-gray-600 mb-4 md:mb-8 px-3">
                        Contact us today for custom printing solutions tailored to your needs.
                    </p>
                    <Link to="mailto:info@xpressprint.in">
                        <button className="bg-[#008000] text-white px-5 md:px-8 py-2.5 md:py-3 rounded-xl hover:bg-[#008000] text-sm md:text-lg font-medium">
                            Contact Us
                        </button>
                    </Link>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default SubcategoryProductsPage;
